import React, { useEffect, useRef, useState } from "react";
import { FiBell, FiCamera, FiChevronRight, FiCreditCard, FiGift, FiLock, FiMail, FiMove, FiX, FiZoomIn } from "react-icons/fi";
import { toast } from "react-toastify";
import { useAuth } from "../../auth/AuthContext";
import { cacheStudentAvatarDisplayUrl, updateStudentAvatarCache } from "../../constants/studentAvatarCache";
import { DEFAULT_STUDENT_AVATARS, getStudentAvatarDisplayUrl } from "../../constants/defaultStudentAvatars";
import { useCachedStudentAvatarUrl } from "../../hooks/useCachedStudentAvatarUrl";
import useStudentPageQuery from "../../hooks/useStudentPageQuery";
import { invalidateStudentPageCache } from "../../services/studentPageCache";
import { createSquareAvatarImage, getGamificationSummary, prepareAvatarImage, selectStudentAvatarPreset, uploadGamificationImage } from "../../services/gamificationService";
import {
    confirmGuardianEmailVerification,
    requestGuardianEmailVerification
} from "../../services/membershipService";
import { loadStudentCommerceDisplay, STUDENT_COMMERCE_DISPLAY_KEY } from "../../services/studentCommerceDisplay";
import { getNicknameSettings, updateNickname } from "../../services/studentSocialService";
import { disableWebPush, enableWebPush, getCurrentWebPushStatus, getWebPushAvailability, getWebPushConfig } from "../../services/webPushService";
import { hasAiPremiumAccess } from "../../constants/membershipPlans";
import { validatePublicNickname } from "../../utils/nicknameValidation";
import StudentProgressOverview from "./StudentProgressOverview";
import StudentAvatarImage from "../fragment/StudentAvatarImage";
import StudentSettingsGroup from "../fragment/StudentSettingsGroup";
import "./css/StudentSettings.scss";

const initial = name => String(name || "A").trim().charAt(0).toUpperCase() || "A";
const AVATAR_CROP_SIZE = 280;
const NICKNAME_CHANGE_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000;
const clamp = (value, minimum, maximum) => Math.min(Math.max(value, minimum), maximum);
const nicknameChangeAvailableAt = settings => {
    const serverTimestamp = Date.parse(settings?.nickname_change_available_at || "");
    if (Number.isFinite(serverTimestamp)) return serverTimestamp;

    const latestActualChange = settings?.nickname_history?.find(item => item.previous_nickname);
    const latestChangeAt = Date.parse(latestActualChange?.changed_at || "");
    return Number.isFinite(latestChangeAt) ? latestChangeAt + NICKNAME_CHANGE_COOLDOWN_MS : 0;
};
const formatNicknameCountdown = milliseconds => {
    const totalSeconds = Math.max(0, Math.ceil(milliseconds / 1000));
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${days} 天 ${hours} 小時 ${minutes} 分 ${seconds} 秒`;
};
const formatNicknameAvailableAt = timestamp => new Intl.DateTimeFormat("zh-TW", {
    timeZone: "Asia/Taipei",
    month: "numeric",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false
}).format(new Date(timestamp));
const friendlyNicknameError = error => {
    const message = error?.message || "";
    if (error instanceof TypeError || /load failed|failed to fetch|networkerror/i.test(message)) {
        return "暱稱服務暫時無法連線，請確認網路後重新整理再試";
    }
    return message || "暱稱資料讀取失敗";
};

const planDisplayPriority = plan => {
    if (plan?.status === "active" || plan?.cancel_at_period_end) return 2;
    if (plan?.stripe_subscription_status === "past_due") return 1;
    return 0;
};

const planDisplayTimestamp = plan => {
    const timestamp = Date.parse(plan?.current_period_end || plan?.ends_at || "");
    return Number.isFinite(timestamp) ? timestamp : 0;
};

const getCropLimits = (draft, zoom = draft?.zoom || 1) => {
    if (!draft?.width || !draft?.height) return { x: 0, y: 0 };
    const scale = Math.max(AVATAR_CROP_SIZE / draft.width, AVATAR_CROP_SIZE / draft.height) * zoom;
    return {
        x: Math.max(0, (draft.width * scale - AVATAR_CROP_SIZE) / 2),
        y: Math.max(0, (draft.height * scale - AVATAR_CROP_SIZE) / 2)
    };
};

const getCropPosition = (draft, offsetX, offsetY, zoom = draft?.zoom || 1) => {
    const limits = getCropLimits(draft, zoom);
    return {
        offsetX: clamp(offsetX, -limits.x, limits.x),
        offsetY: clamp(offsetY, -limits.y, limits.y)
    };
};

// 教材與方案 details are hidden for now; flip to bring the section back.
const SHOW_PLAN_DETAILS = false;

// One component, three pages: 我的進度 (what to do next), 個人資料 (picture and nickname) and
// 通知設定 (push and the parent's e-mail). Account links live in the menu.
function StudentSettings({ view = "progress" }) {
    const profileView = view === "profile";
    const notificationsView = view === "notifications";
    const { firebaseUser, studentProfile, setStudentProfile, refreshStudentProfile } = useAuth();
    const fileInputRef = useRef(null);
    const enabled = Boolean(firebaseUser);
    const summaryQuery = useStudentPageQuery("summary", () => getGamificationSummary(firebaseUser), { enabled });
    const commerceQuery = useStudentPageQuery(STUDENT_COMMERCE_DISPLAY_KEY, () => loadStudentCommerceDisplay(firebaseUser), { enabled });
    const nicknameQuery = useStudentPageQuery("settings:nickname", async () => {
        const result = await getNicknameSettings(firebaseUser);
        return {
            profile: { nickname: result?.profile?.nickname || "" },
            nickname_history: result?.nickname_history?.map(({ id, previous_nickname, new_nickname, changed_at }) => ({ id, previous_nickname, new_nickname, changed_at })) || [],
            nickname_change_available_at: result?.nickname_change_available_at || null
        };
    }, { enabled });
    const summary = summaryQuery.data;
    const commerce = commerceQuery.data;
    const nicknameSettings = nicknameQuery.data || { profile: null, nickname_history: [] };
    const setSummary = summaryQuery.update;
    const setNicknameSettings = nicknameQuery.update;
    const [nicknameDraft, setNicknameDraft] = useState(studentProfile?.nickname || "");
    const nicknameEditedRef = useRef(false);
    const [nicknameError, setNicknameError] = useState("");
    const [nicknameConfirmation, setNicknameConfirmation] = useState(null);
    const [savingNickname, setSavingNickname] = useState(false);
    const [nicknameClock, setNicknameClock] = useState(() => Date.now());
    const [uploading, setUploading] = useState(false);
    const [guardianEmail, setGuardianEmail] = useState("");
    const [savingGuardian, setSavingGuardian] = useState(false);
    const [guardianVerification, setGuardianVerification] = useState({ requestId: null, maskedEmail: "", code: "" });
    const [pushConfig, setPushConfig] = useState(null);
    const [pushStatus, setPushStatus] = useState(null);
    const [pushBusy, setPushBusy] = useState(false);
    const [avatarDraft, setAvatarDraft] = useState(null);
    const [avatarConfirmation, setAvatarConfirmation] = useState(null);
    const [showAvatarPresets, setShowAvatarPresets] = useState(false);
    const avatarDragRef = useRef(null);
    const nicknameAvailableAt = nicknameChangeAvailableAt(nicknameSettings);
    const nicknameCooldownActive = nicknameAvailableAt > nicknameClock;
    const nicknameCountdown = nicknameCooldownActive
        ? formatNicknameCountdown(nicknameAvailableAt - nicknameClock)
        : "";

    const load = () => Promise.all([summaryQuery.refresh(), commerceQuery.refresh(), nicknameQuery.refresh()]);
    useEffect(() => {
        if (!nicknameEditedRef.current) setNicknameDraft(nicknameSettings.profile?.nickname || studentProfile?.nickname || "");
    }, [nicknameSettings.profile?.nickname, studentProfile?.nickname]);
    useEffect(() => {
        if (summary?.profile?.avatar_url && firebaseUser?.uid) {
            cacheStudentAvatarDisplayUrl(summary.profile.avatar_url, {
                ownerUid: firebaseUser.uid,
                sourceKey: studentProfile?.user_image || studentProfile?.userimage
            });
        }
    }, [summary, firebaseUser?.uid, studentProfile?.user_image, studentProfile?.userimage]);
    useEffect(() => {
        if (!firebaseUser) return undefined;
        let cancelled = false;
        const availability = getWebPushAvailability();
        setPushStatus({ ...availability, active: false });
        if (availability.supported) {
            getWebPushConfig(firebaseUser).then(async config => {
                if (cancelled) return;
                setPushConfig(config);
                if (config.enabled) {
                    const status = await getCurrentWebPushStatus(firebaseUser);
                    if (!cancelled) setPushStatus(status);
                }
            }).catch(() => {
                if (!cancelled) setPushConfig({ enabled: false });
            });
        }
        return () => { cancelled = true; };
    }, [firebaseUser]);

    const togglePush = async () => {
        if (!firebaseUser || pushBusy || !pushConfig?.enabled) return;
        setPushBusy(true);
        try {
            if (pushStatus?.active) await disableWebPush(firebaseUser);
            else await enableWebPush(firebaseUser, pushConfig.public_key);
            const status = await getCurrentWebPushStatus(firebaseUser);
            setPushStatus(status);
            toast.success(status.active ? "此裝置已開啟推播" : "此裝置已關閉推播");
        } catch (error) {
            setPushStatus(getWebPushAvailability());
            toast.error(error.message || "推播設定失敗");
        } finally {
            setPushBusy(false);
        }
    };
    useEffect(() => { setGuardianEmail(commerce?.guardian?.email || studentProfile?.guardian?.email || ""); }, [commerce?.guardian?.email, studentProfile?.guardian?.email]);
    useEffect(() => {
        if (!nicknameCooldownActive) return undefined;
        const timer = window.setInterval(() => setNicknameClock(Date.now()), 1000);
        return () => window.clearInterval(timer);
    }, [nicknameAvailableAt, nicknameCooldownActive]);
    useEffect(() => () => {
        if (avatarConfirmation?.revokePreview && avatarConfirmation.previewUrl) {
            URL.revokeObjectURL(avatarConfirmation.previewUrl);
        }
    }, [avatarConfirmation]);

    const closeAvatarEditor = () => {
        setAvatarDraft(current => {
            if (current?.previewUrl) URL.revokeObjectURL(current.previewUrl);
            return null;
        });
        avatarDragRef.current = null;
    };

    const handleAvatarChange = event => {
        const selectedFile = event.target.files?.[0];
        event.target.value = "";
        if (!selectedFile || !firebaseUser) return;

        if (!/^image\/(jpeg|png|webp)$/.test(selectedFile.type)) {
            toast.error("只支援 JPG、PNG、WebP 圖片");
            return;
        }
        if (selectedFile.size > 20 * 1024 * 1024) {
            toast.error("原始照片請控制在 20MB 以內");
            return;
        }
        setAvatarDraft({
            file: selectedFile,
            previewUrl: URL.createObjectURL(selectedFile),
            width: 0,
            height: 0,
            zoom: 1,
            offsetX: 0,
            offsetY: 0
        });
    };

    const reviewCustomAvatar = async () => {
        if (!avatarDraft || !firebaseUser) return;
        setUploading(true);
        try {
            const croppedFile = await createSquareAvatarImage(avatarDraft.file, {
                zoom: avatarDraft.zoom,
                offsetX: avatarDraft.offsetX,
                offsetY: avatarDraft.offsetY,
                previewSize: AVATAR_CROP_SIZE
            });
            const file = await prepareAvatarImage(croppedFile);
            setAvatarConfirmation({
                kind: "upload",
                file,
                label: "自選照片",
                previewUrl: URL.createObjectURL(file),
                revokePreview: true
            });
        } catch (error) {
            toast.error(error.message || "頭像預覽建立失敗");
        } finally {
            setUploading(false);
        }
    };

    const reviewPresetAvatar = avatar => {
        if (!firebaseUser || uploading) return;
        setAvatarConfirmation({
            kind: "preset",
            avatar,
            label: avatar.name,
            previewUrl: getStudentAvatarDisplayUrl(avatar.path, 256),
            revokePreview: false
        });
    };

    const closeAvatarConfirmation = () => {
        if (uploading) return;
        setAvatarConfirmation(null);
    };

    const confirmAvatarChange = async () => {
        if (!firebaseUser || !avatarConfirmation || uploading) return;
        setUploading(true);
        try {
            const result = avatarConfirmation.kind === "preset"
                ? await selectStudentAvatarPreset(firebaseUser, avatarConfirmation.avatar.path)
                : await uploadGamificationImage(firebaseUser, "avatar", avatarConfirmation.file);
            setSummary(current => current ? {
                ...current,
                profile: { ...current.profile, avatar_url: result.image_url }
            } : current);
            const nextProfile = updateStudentAvatarCache(studentProfile || {}, {
                imageUrl: result.image_url,
                path: result.path,
                ownerUid: firebaseUser.uid,
                previewBlob: avatarConfirmation.kind === "upload" ? avatarConfirmation.file : undefined
            });
            setStudentProfile(nextProfile);
            invalidateStudentPageCache(firebaseUser.uid, ["summary"]);
            setAvatarConfirmation(null);
            closeAvatarEditor();
            toast.success(avatarConfirmation.kind === "preset" ? `已套用${avatarConfirmation.label}` : "頭像已更新");
        } catch (error) {
            toast.error(error.message || "頭像更新失敗");
        } finally {
            setUploading(false);
        }
    };

    const updateAvatarPosition = (offsetX, offsetY, zoom = avatarDraft?.zoom || 1) => {
        setAvatarDraft(current => current ? { ...current, ...getCropPosition(current, offsetX, offsetY, zoom), zoom } : current);
    };

    const handleAvatarZoom = event => {
        const zoom = Number(event.target.value);
        setAvatarDraft(current => current ? {
            ...current,
            ...getCropPosition(current, current.offsetX, current.offsetY, zoom),
            zoom
        } : current);
    };

    const beginAvatarDrag = ({ pointerId, clientX, clientY }) => {
        if (!avatarDraft?.width || !avatarDraft?.height) return;
        avatarDragRef.current = { pointerId, x: clientX, y: clientY, offsetX: avatarDraft.offsetX, offsetY: avatarDraft.offsetY };
    };

    const continueAvatarDrag = ({ pointerId, clientX, clientY }) => {
        const drag = avatarDragRef.current;
        if (!drag || drag.pointerId !== pointerId) return;
        updateAvatarPosition(drag.offsetX + clientX - drag.x, drag.offsetY + clientY - drag.y);
    };

    const endAvatarDrag = pointerId => {
        if (avatarDragRef.current?.pointerId === pointerId) avatarDragRef.current = null;
    };

    const startAvatarPointerDrag = event => {
        event.preventDefault();
        try {
            event.currentTarget.setPointerCapture?.(event.pointerId);
        } catch {
            // Older Safari may not support pointer capture; movement still works while inside the crop area.
        }
        beginAvatarDrag({ pointerId: `pointer-${event.pointerId}`, clientX: event.clientX, clientY: event.clientY });
    };

    const moveAvatarPointerDrag = event => {
        event.preventDefault();
        continueAvatarDrag({ pointerId: `pointer-${event.pointerId}`, clientX: event.clientX, clientY: event.clientY });
    };

    const stopAvatarPointerDrag = event => {
        endAvatarDrag(`pointer-${event.pointerId}`);
    };

    const getTrackedTouch = touches => {
        const activePointerId = avatarDragRef.current?.pointerId;
        return Array.from(touches || []).find(touch => `touch-${touch.identifier}` === activePointerId);
    };

    const startAvatarTouchDrag = event => {
        if (String(avatarDragRef.current?.pointerId || "").startsWith("pointer-")) return;
        const touch = event.changedTouches?.[0];
        if (!touch) return;
        event.preventDefault();
        beginAvatarDrag({ pointerId: `touch-${touch.identifier}`, clientX: touch.clientX, clientY: touch.clientY });
    };

    const moveAvatarTouchDrag = event => {
        const touch = getTrackedTouch(event.touches);
        if (!touch) return;
        event.preventDefault();
        continueAvatarDrag({ pointerId: `touch-${touch.identifier}`, clientX: touch.clientX, clientY: touch.clientY });
    };

    const stopAvatarTouchDrag = event => {
        const touch = getTrackedTouch(event.changedTouches);
        if (touch) endAvatarDrag(`touch-${touch.identifier}`);
    };

    const requestNicknameSave = event => {
        event.preventDefault();
        if (!firebaseUser) return;
        if (nicknameCooldownActive) {
            toast.info(`距離下次修改還有 ${nicknameCountdown}`);
            return;
        }
        const validationError = validatePublicNickname(nicknameDraft);
        if (validationError) {
            setNicknameError(validationError);
            return;
        }
        const nextNickname = nicknameDraft.trim().replace(/\s+/g, " ");
        const currentNickname = nicknameSettings.profile?.nickname || studentProfile?.nickname || "";
        if (currentNickname.toLocaleLowerCase("en-US") === nextNickname.toLocaleLowerCase("en-US")) {
            toast.info("公開暱稱沒有變更");
            return;
        }
        setNicknameError("");
        setNicknameConfirmation({ currentNickname, nextNickname });
    };

    const cancelNicknameConfirmation = () => {
        if (!savingNickname) setNicknameConfirmation(null);
    };

    const confirmNicknameChange = async () => {
        if (!firebaseUser || !nicknameConfirmation) return;
        setSavingNickname(true);
        setNicknameError("");
        try {
            const result = await updateNickname(firebaseUser, nicknameConfirmation.nextNickname);
            const savedNickname = result?.profile?.nickname || nicknameConfirmation.nextNickname;
            nicknameEditedRef.current = false;
            setNicknameDraft(savedNickname);
            setNicknameSettings(() => ({
                profile: result?.profile || { nickname: savedNickname },
                nickname_history: result?.nickname_history || [],
                nickname_change_available_at: result?.nickname_change_available_at || null
            }));
            invalidateStudentPageCache(firebaseUser.uid, ["settings:nickname"]);
            setNicknameClock(Date.now());
            setStudentProfile(current => current ? { ...current, nickname: savedNickname } : current);
            setNicknameConfirmation(null);
            toast.success("公開暱稱已更新");
        } catch (error) {
            if (error?.code === "NICKNAME_CHANGE_COOLDOWN" && error?.details?.nickname_change_available_at) {
                setNicknameSettings(current => ({
                    ...current,
                    nickname_change_available_at: error.details.nickname_change_available_at
                }));
                setNicknameClock(Date.now());
            }
            const message = friendlyNicknameError(error);
            setNicknameError(message);
            toast.error(message);
        } finally {
            setSavingNickname(false);
        }
    };

    const sendGuardianVerification = async event => {
        event.preventDefault();
        if (!firebaseUser) return;
        setSavingGuardian(true);
        try {
            const result = await requestGuardianEmailVerification(firebaseUser, guardianEmail);
            if (result?.already_verified) {
                toast.info("這個家長 Email 已完成驗證");
                return;
            }
            setGuardianVerification({
                requestId: result?.request_id || null,
                maskedEmail: result?.masked_email || "家長信箱",
                code: ""
            });
            toast.success("驗證碼已寄出，請家長查看收件匣與垃圾郵件");
        } catch (error) { toast.error(error.message || "無法寄送家長 Email 驗證碼"); }
        finally { setSavingGuardian(false); }
    };

    const confirmGuardianVerification = async event => {
        event.preventDefault();
        if (!firebaseUser || !guardianVerification.requestId) return;
        if (!/^\d{6}$/.test(guardianVerification.code)) {
            toast.error("請輸入信件中的 6 位數驗證碼");
            return;
        }
        setSavingGuardian(true);
        try {
            await confirmGuardianEmailVerification(firebaseUser, guardianVerification.requestId, guardianVerification.code);
            if (refreshStudentProfile) await refreshStudentProfile();
            await load();
            setGuardianVerification({ requestId: null, maskedEmail: "", code: "" });
            toast.success("家長 Email 已驗證並更新");
        } catch (error) { toast.error(error.message || "家長 Email 驗證失敗"); }
        finally { setSavingGuardian(false); }
    };

    const profile = studentProfile || {};
    const currentGuardian = commerce?.guardian || profile?.guardian || null;
    const guardianVerified = Boolean(currentGuardian?.email && currentGuardian?.email_verified_at);
    const publicDisplayName = nicknameSettings.profile?.nickname || profile.nickname || profile.chinese_name || profile.name || "學生";
    const avatarUrl = useCachedStudentAvatarUrl(summary?.profile?.avatar_url || studentProfile?.avatar_url, {
        ownerUid: firebaseUser?.uid,
        sourceKey: studentProfile?.user_image || studentProfile?.userimage
    });
    const avatarDisplayUrl = getStudentAvatarDisplayUrl(avatarUrl, 256);
    const effectiveAccess = profile?.membership?.effective_access;
    const hasAiPremium = hasAiPremiumAccess(effectiveAccess);
    const hasAiMaterials = profile?.membership?.effective_access?.features?.ai_materials === true;
    const isActiveAcademyStudent = effectiveAccess?.plan_codes?.includes("academy_internal") === true;
    const statusLabel = !commerce ? commerceQuery.loading ? "讀取中…" : "暫時無法讀取" : commerce.enrollment_status === "active" ? "在校" : commerce.enrollment_status === "scheduled_departure" ? "預定離校" : commerce.enrollment_status === "departed" ? "離校" : "非在校生";
    const currentEnrollment = commerce?.current_enrollment || null;
    const enrollmentRecord = currentEnrollment || commerce?.enrollment_history?.[0] || null;
    const directBooks = commerce?.direct_entitlements || [];
    const booksBySource = source => directBooks.filter(item => item.source === source);
    const bookNames = items => [...new Set(items.map(item => item.books?.name).filter(Boolean))].join("、") || "—";
    const planValue = plan => Array.isArray(plan?.subscription_plans) ? plan.subscription_plans[0] : plan?.subscription_plans;
    const planCode = plan => planValue(plan)?.code || "";
    const planName = plan => {
        const value = planValue(plan);
        if (["ai_materials_addon_monthly", "ai_materials_general_monthly"].includes(value?.code)) return "AI 教材與發音練習";
        return value?.name || value?.code || "方案";
    };
    const planStatus = plan => {
        const endDate = (plan.current_period_end || plan.ends_at)?.slice(0, 10) || null;
        if (plan.stripe_subscription_status === "past_due") return "付款失敗";
        if (["expired", "revoked"].includes(plan.status)) return endDate ? `已結束（${endDate}）` : "已結束";
        if (plan.cancel_at_period_end) return `本期結束取消（${endDate || "—"}）`;
        if (plan.stripe_subscription_status === "canceled") return endDate ? `已取消（${endDate}）` : "已取消";
        if (plan.current_period_end) return `續訂日 ${plan.current_period_end.slice(0, 10)}`;
        if (plan.ends_at) return `到期日 ${plan.ends_at.slice(0, 10)}`;
        return plan.status === "active" ? "使用中" : plan.status;
    };
    const visiblePlans = Array.from((commerce?.plans || [])
        .filter(plan => planCode(plan) !== "academy_internal")
        .reduce((plans, plan) => {
            const code = planCode(plan) || `grant:${plan.id}`;
            const current = plans.get(code);
            const priority = planDisplayPriority(plan);
            const currentPriority = planDisplayPriority(current);
            if (!current
                || priority > currentPriority
                || (priority === currentPriority && planDisplayTimestamp(plan) > planDisplayTimestamp(current))) {
                plans.set(code, plan);
            }
            return plans;
        }, new Map())
        .values());

    return (
        <main className={`student-settings-page${profileView || notificationsView ? " is-profile" : ""}`}>
            <section className="student-settings-hero">
                <h1>{profileView ? "個人資料" : notificationsView ? "通知設定" : "我的進度"}</h1>
            </section>


            {!profileView && !notificationsView && <StudentProgressOverview />}

            {(profileView || notificationsView) && (commerceQuery.error || nicknameQuery.error) && <p role="status">部分設定暫時無法更新，已有資料會保留。<button type="button" onClick={load}>重新讀取設定</button></p>}
            {profileView && <section className="student-settings-profile-card">
                <div className="student-settings-avatar-wrap">
                    {avatarDisplayUrl
                        ? <StudentAvatarImage src={avatarDisplayUrl} className="student-settings-avatar" alt={`${publicDisplayName} 的頭像`} />
                        : <div className="student-settings-avatar fallback">{initial(publicDisplayName)}</div>}
                    <button type="button" className="student-settings-avatar-button" onClick={() => fileInputRef.current?.click()} disabled={uploading} aria-label="更換學生頭像"><FiCamera /></button>
                    <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={handleAvatarChange} />
                </div>
                <div className="student-settings-profile-copy">
                    <h2>{publicDisplayName}</h2>
                    <p>{[profile.english_name, profile.class ? `${profile.class} 班` : ""].filter(Boolean).join("　·　")}</p>
                    {uploading && <small role="status">正在處理頭像…</small>}
                </div>
                    <form className={`student-settings-nickname-form student-settings-profile-nickname-form ${nicknameCooldownActive ? "is-locked" : ""}`} onSubmit={requestNicknameSave}>
                        <label htmlFor="student-settings-nickname">公開暱稱</label>
                        <div>
                            <input
                                id="student-settings-nickname"
                                value={nicknameDraft}
                                onChange={event => { nicknameEditedRef.current = true; setNicknameDraft(event.target.value); if (nicknameError) setNicknameError(""); }}
                                maxLength="20"
                                placeholder="例如 Alan Fox"
                                aria-invalid={Boolean(nicknameError)}
                                aria-describedby="student-settings-nickname-help"
                                disabled={savingNickname || nicknameCooldownActive}
                                required
                            />
                            <button type="submit" disabled={savingNickname || nicknameCooldownActive}>{savingNickname ? "儲存中…" : nicknameCooldownActive ? "暫時無法改名" : "儲存暱稱"}</button>
                        </div>
                        <small id="student-settings-nickname-help" className={nicknameError ? "is-error" : ""}>
                            {nicknameError || (nicknameQuery.error && friendlyNicknameError(nicknameQuery.error)) || (nicknameCooldownActive ? (
                                <span className="student-settings-nickname-countdown" role="status">
                                    <FiLock />距離下次修改還有 <strong>{nicknameCountdown}</strong><br />
                                    可於台灣時間 {formatNicknameAvailableAt(nicknameAvailableAt)} 後再次修改。
                                </span>
                            ) : "每 7 天可以改一次")}
                        </small>
                    </form>
                <details className="student-settings-avatar-presets" onToggle={event => setShowAvatarPresets(event.currentTarget.open)}>
                    <summary>選擇預設頭像</summary>
                    <div className="student-settings-avatar-preset-grid">
                        {showAvatarPresets && DEFAULT_STUDENT_AVATARS.map(avatar => (
                            <button key={avatar.id} type="button" onClick={() => reviewPresetAvatar(avatar)} disabled={uploading} aria-pressed={avatarUrl === avatar.path} aria-label={`使用${avatar.name}頭像`}>
                                <StudentAvatarImage className="student-settings-avatar-preset-image" src={getStudentAvatarDisplayUrl(avatar.path, 160)} alt="" loading="lazy" decoding="async" />
                                <span>{avatar.name}</span>
                            </button>
                        ))}
                    </div>
                </details>
            </section>}

            {
                avatarDraft && !avatarConfirmation && (
                    <div className="student-avatar-editor-backdrop" role="presentation">
                        <section className="student-avatar-editor" role="dialog" aria-modal="true" aria-labelledby="avatar-editor-title">
                            <header>
                                <div><span>ADJUST YOUR PHOTO</span><h2 id="avatar-editor-title">調整正方形頭像</h2></div>
                                <button type="button" onClick={closeAvatarEditor} disabled={uploading} aria-label="關閉頭像調整視窗"><FiX /></button>
                            </header>
                            <p>拖移照片，讓想保留的內容落在方形範圍內。儲存後每個學生的頭像都會是略圓角的正方形。</p>
                            <div className="student-avatar-crop-canvas" onPointerDown={startAvatarPointerDrag} onPointerMove={moveAvatarPointerDrag} onPointerUp={stopAvatarPointerDrag} onPointerCancel={stopAvatarPointerDrag} onLostPointerCapture={stopAvatarPointerDrag} onTouchStart={startAvatarTouchDrag} onTouchMove={moveAvatarTouchDrag} onTouchEnd={stopAvatarTouchDrag} onTouchCancel={stopAvatarTouchDrag}>
                                {!avatarDraft.width && <span className="student-avatar-crop-loading" role="status" aria-label="頭貼載入中" />}
                                <img
                                    src={avatarDraft.previewUrl}
                                    alt="頭像裁切預覽"
                                    draggable="false"
                                    onLoad={event => {
                                        const { naturalWidth, naturalHeight } = event.currentTarget;
                                        setAvatarDraft(current => current ? { ...current, width: naturalWidth, height: naturalHeight } : current);
                                    }}
                                    style={avatarDraft.width && avatarDraft.height ? (() => {
                                        const scale = Math.max(AVATAR_CROP_SIZE / avatarDraft.width, AVATAR_CROP_SIZE / avatarDraft.height) * avatarDraft.zoom;
                                        return { width: `${avatarDraft.width * scale}px`, height: `${avatarDraft.height * scale}px`, left: `calc(50% + ${avatarDraft.offsetX}px)`, top: `calc(50% + ${avatarDraft.offsetY}px)` };
                                    })() : undefined}
                                />
                                <span className="student-avatar-crop-frame" aria-hidden="true"><FiMove /><small>拖移照片</small></span>
                            </div>
                            <label className="student-avatar-zoom"><span><FiZoomIn />縮放</span><input aria-label="頭像縮放" type="range" min="1" max="3" step="0.05" value={avatarDraft.zoom} onChange={handleAvatarZoom} /><strong>{Math.round(avatarDraft.zoom * 100)}%</strong></label>
                            <div className="student-avatar-editor-actions"><button type="button" className="student-avatar-editor-cancel" onClick={closeAvatarEditor} disabled={uploading}>取消</button><button type="button" className="student-avatar-editor-save" onClick={reviewCustomAvatar} disabled={uploading || !avatarDraft.width}>{uploading ? "建立預覽中…" : "預覽並確認"}</button></div>
                        </section>
                    </div>
                )
            }

            {
                avatarConfirmation && (
                    <div className="student-avatar-editor-backdrop" role="presentation">
                        <section className="student-avatar-editor student-avatar-confirmation" role="alertdialog" aria-modal="true" aria-labelledby="avatar-confirmation-title">
                            <header>
                                <div><span>FINAL CONFIRMATION</span><h2 id="avatar-confirmation-title">確認更換頭像</h2></div>
                                <button type="button" onClick={closeAvatarConfirmation} disabled={uploading} aria-label="關閉頭像確認視窗"><FiX /></button>
                            </header>
                            <p>這是最後一步。請確認下方頭像會顯示在個人資料與排行榜；只有按下確認才會真正儲存。</p>
                            <div className="student-avatar-confirmation-comparison">
                                <div>
                                    <span>目前頭像</span>
                                    {avatarDisplayUrl
                                        ? <StudentAvatarImage className="student-avatar-confirmation-image" src={avatarDisplayUrl} alt="目前使用的頭像" />
                                        : <div className="student-avatar-confirmation-fallback" aria-label="目前使用的文字頭像">{initial(publicDisplayName)}</div>}
                                </div>
                                <strong aria-hidden="true">→</strong>
                                <div className="pending">
                                    <span>即將套用</span>
                                    <StudentAvatarImage className="student-avatar-confirmation-image" src={avatarConfirmation.previewUrl} alt={`即將套用的${avatarConfirmation.label}頭像`} />
                                </div>
                            </div>
                            <div className="student-avatar-editor-actions">
                                <button type="button" className="student-avatar-editor-cancel" onClick={closeAvatarConfirmation} disabled={uploading}>{avatarConfirmation.kind === "upload" ? "返回調整" : "取消"}</button>
                                <button type="button" className="student-avatar-editor-save" onClick={confirmAvatarChange} disabled={uploading}>{uploading ? "儲存中…" : "確認更換頭像"}</button>
                            </div>
                        </section>
                    </div>
                )
            }

            {
                nicknameConfirmation && (
                    <div className="student-avatar-editor-backdrop" role="presentation">
                        <section className="student-avatar-editor student-nickname-confirmation" role="alertdialog" aria-modal="true" aria-labelledby="nickname-confirmation-title" aria-describedby="nickname-confirmation-description">
                            <header>
                                <div><span>CONFIRM NICKNAME</span><h2 id="nickname-confirmation-title">確認修改公開暱稱</h2></div>
                                <button type="button" onClick={cancelNicknameConfirmation} disabled={savingNickname} aria-label="關閉暱稱確認視窗"><FiX /></button>
                            </header>
                            <p id="nickname-confirmation-description">公開暱稱會顯示在排行榜、好友與學生首頁。確認後，下一次實際更名須等待 7 天。</p>
                            <div className="student-nickname-confirmation-details">
                                <span>即將使用的公開暱稱</span>
                                <strong>{nicknameConfirmation.nextNickname}</strong>
                            </div>
                            <div className="student-avatar-editor-actions">
                                <button type="button" className="student-avatar-editor-cancel" onClick={cancelNicknameConfirmation} disabled={savingNickname}>返回修改</button>
                                <button type="button" className="student-avatar-editor-save" onClick={confirmNicknameChange} disabled={savingNickname}>{savingNickname ? "儲存中…" : "確認修改暱稱"}</button>
                            </div>
                        </section>
                    </div>
                )
            }

            {SHOW_PLAN_DETAILS && <StudentSettingsGroup title="教材與方案" description="目前教材與使用期限；方案資料可請家長一起確認">
            <section className="student-settings-grid">
                <article className="student-settings-panel">
                    <header><FiCreditCard /><div><span>MEMBERSHIP</span><h2>我的帳號與使用狀態</h2></div></header>
                    <dl className="student-settings-data-list">
                        <div><dt>AI 教材與發音方案</dt><dd>{hasAiPremium ? isActiveAcademyStudent ? "英文班方案已包含" : "已加購" : "未開通"}</dd></div>
                        <div><dt>AI 教材與發音練習</dt><dd>{hasAiMaterials ? "兩項皆可使用" : "目前不可使用"}</dd></div>
                        <div><dt>帳號類型</dt><dd>{profile.learner_type === "academy_student" ? "英文班學生" : profile.learner_type === "textbook_customer" ? "教材購買者" : "試用／一般學生"}</dd></div>
                        <div><dt>在校狀態</dt><dd>{statusLabel}</dd></div>
                        <div><dt>入學日期</dt><dd>{enrollmentRecord?.enrolled_at || "—"}</dd></div>
                        <div><dt>預定離校</dt><dd>{enrollmentRecord?.scheduled_departure_at || "—"}</dd></div>
                        <div><dt>實際離校</dt><dd>{enrollmentRecord?.departed_at || "—"}</dd></div>
                    </dl>
                </article>
                <article className="student-settings-panel">
                    <header><FiGift /><div><span>BOOK OWNERSHIP</span><h2>教材從哪裡取得</h2></div></header>
                    <dl className="student-settings-data-list">
                        <div><dt>班級取得教材</dt><dd>{commerce?.class_books?.map(book => book?.name).filter(Boolean).join("、") || "—"}</dd></div>
                        <div><dt>離校永久保留教材</dt><dd>{bookNames(booksBySource("academy_history"))}</dd></div>
                        <div><dt>已購買教材</dt><dd>{bookNames(booksBySource("material_purchase"))}</dd></div>
                        <div><dt>管理員贈送</dt><dd>{bookNames(booksBySource("admin_grant"))}</dd></div>
                        <div><dt>開通碼教材</dt><dd>{bookNames(booksBySource("activation_code"))}</dd></div>
                    </dl>
                    <p>教材擁有權永久保留；網站使用權由班級、90 天贈送、試用或會員方案分別疊加。</p>
                </article>
                <article className="student-settings-panel">
                    <header><FiCreditCard /><div><span>PLAN STATUS</span><h2>家長協助：方案與使用期限</h2></div></header>
                    <dl className="student-settings-data-list">
                        {visiblePlans.length ? visiblePlans.map(plan => <div key={plan.id}><dt>{planName(plan)}</dt><dd>{planStatus(plan)}</dd></div>) : <div><dt>方案</dt><dd>{!commerce ? commerceQuery.loading ? "讀取中…" : "暫時無法讀取" : "目前無基本會員或 AI 教材與發音練習方案"}</dd></div>}
                    </dl>
                </article>
            </section>
            </StudentSettingsGroup>}

            {notificationsView && <section className="student-settings-list" aria-labelledby="student-settings-notify-heading">
                <h2 id="student-settings-notify-heading" className="student-settings-visually-hidden">通知</h2>
                <div className="student-settings-row">
                    <FiBell aria-hidden="true" />
                    <div><strong id="student-settings-push-label">手機推播通知</strong><small aria-live="polite">{pushStatus?.reason || (pushConfig?.enabled === false ? "推播服務暫時未開放；網站內通知仍可使用。" : pushStatus?.active ? "此裝置已開啟：新作業與教材期限提醒" : "此裝置尚未開啟")}</small></div>
                    <button type="button" role="switch" className="student-settings-switch" aria-checked={Boolean(pushStatus?.active)} aria-labelledby="student-settings-push-label" aria-busy={pushBusy} onClick={togglePush} disabled={pushBusy || !pushConfig?.enabled || (!pushStatus?.active && !pushStatus?.supported)}><span aria-hidden="true" /></button>
                </div>
                <details className="student-settings-row-details">
                    <summary className="student-settings-row">
                        <FiMail aria-hidden="true" />
                        <div><strong>家長 Email</strong><small>{currentGuardian?.email ? `${currentGuardian.email} · ${guardianVerified ? "已驗證" : "尚未驗證"}` : "尚未設定"}</small></div>
                        <FiChevronRight aria-hidden="true" />
                    </summary>
                    <div className="student-settings-row-body">
                    <p>請家長協助填寫可收信的 Email。付款與重要通知只會寄到已驗證的家長 Email；更換時，驗證成功前仍保留原本的信箱。</p>
                    {guardianVerification.requestId ? (
                        <form className="student-settings-birthday-form" onSubmit={confirmGuardianVerification}>
                            <p>驗證碼已寄到 {guardianVerification.maskedEmail}。</p>
                            <label className="student-settings-birthday-field"><span>6 位數驗證碼</span><input type="text" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={6} required value={guardianVerification.code} onChange={event => setGuardianVerification(current => ({ ...current, code: event.target.value.replace(/\D/g, "") }))} /></label>
                            <button type="submit" disabled={savingGuardian}>{savingGuardian ? "驗證中…" : "確認驗證碼並更新"}</button>
                            <button type="button" className="student-settings-secondary-button" disabled={savingGuardian} onClick={() => setGuardianVerification({ requestId: null, maskedEmail: "", code: "" })}>更換 Email／重新寄送</button>
                        </form>
                    ) : (
                        <form className="student-settings-birthday-form" onSubmit={sendGuardianVerification}>
                            <label className="student-settings-birthday-field"><span>{currentGuardian?.email ? "新的家長 Email" : "家長 Email"}</span><input type="email" required value={guardianEmail} onChange={event => setGuardianEmail(event.target.value)} placeholder="parent@example.com" /></label>
                            <button type="submit" disabled={savingGuardian}>{savingGuardian ? "寄送中…" : "寄送驗證碼"}</button>
                        </form>
                    )}
                    </div>
                </details>
            </section>}
        </main >
    );
}

export default StudentSettings;
